// FILE: src/pages/admin/AdminSupportReportPage.jsx
// Service levels (WP-6, MTO 10.2, 11.2.1): how quickly support requests
// opened in a date range were answered and resolved against the 48-hour
// target. GET /api/admin/support/sla?from=YYYY-MM-DD&to=YYYY-MM-DD
// (Toronto days, `to` inclusive; default the last 30 days) →
//   { from, to, generatedAt, slaHours,
//     counts: { total, open, resolved, breached, resolvedWithinSla, openOverdue, reopened },
//     resolutionHours / firstResponseHours: { count, median, average, max },
//     byCategory / byChannel: { KEY: { total, open, breached } },
//     breached: [{ _id, reference, user, category, channel, status, createdAt, slaDueAt, resolvedAt, overdueHours }] }
// The dates are kept in the URL (?from=&to=). Figures are words and
// numbers; the per-kind and per-channel tables are the only breakdowns.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import fetchWithAuth from "../../utils/fetchWithAuth";
import { EmptyState, ErrorState, LoadingState, PageHeader, Section, StatCard, visuallyHidden } from "../../components/ui";
import { FigureGroup, ResponsiveTable, TicketStatusChip } from "../../components/admin/AdminBits";
import {
  DASH,
  SLA_HOURS,
  formatDate,
  formatDateTime,
  formatHours,
  formatNumber,
  personName
} from "../../components/admin/format";
import { CHANNELS, channelLabel, hoursText } from "../../components/admin/supportSla";
import { TICKET_CATEGORIES } from "../../components/support/ticketDisplay";
import { ticketCategoryLabel } from "../../i18n/labels";

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;

function dayKey(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function defaultRange() {
  const today = new Date();
  return { from: dayKey(new Date(today.getTime() - 30 * DAY_MS)), to: dayKey(today) };
}

function pct(part, whole) {
  return whole > 0 ? Math.round((part / whole) * 100) : null;
}

/** "Average 4.1 h · longest 20 h · 7 requests" for a { count, median, average, max } block. */
function spreadNote(block, what) {
  if (!block || !block.count) return `No ${what} in this period yet.`;
  return `Average ${formatHours(block.average)} · longest ${formatHours(block.max)} · ${formatNumber(block.count)} ${
    block.count === 1 ? "request" : "requests"
  }`;
}

function BreakdownTable({ label, firstColumn, rows }) {
  return (
    <ResponsiveTable label={label} minWidth={420}>
      <TableHead>
        <TableRow>
          <TableCell component="th" scope="col">
            {firstColumn}
          </TableCell>
          <TableCell component="th" scope="col" align="right">
            Requests
          </TableCell>
          <TableCell component="th" scope="col" align="right">
            Still open
          </TableCell>
          <TableCell component="th" scope="col" align="right">
            Over {SLA_HOURS} hours
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.key}>
            <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
              {r.label}
            </TableCell>
            <TableCell align="right">{formatNumber(r.total)}</TableCell>
            <TableCell align="right">{formatNumber(r.open)}</TableCell>
            <TableCell align="right" sx={{ fontWeight: r.breached > 0 ? 650 : 400 }}>
              {formatNumber(r.breached)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </ResponsiveTable>
  );
}

function breakdownRows(group, keys, labelFor) {
  const src = group && typeof group === "object" ? group : {};
  const extra = Object.keys(src).filter((k) => !keys.includes(k));
  return [...keys, ...extra].map((k) => ({
    key: k,
    label: labelFor(k),
    total: Number(src[k]?.total) || 0,
    open: Number(src[k]?.open) || 0,
    breached: Number(src[k]?.breached) || 0
  }));
}

export default function AdminSupportReportPage() {
  const [params, setParams] = useSearchParams();
  const defaults = defaultRange();
  const from = DATE_RE.test(params.get("from") || "") ? params.get("from") : defaults.from;
  const to = DATE_RE.test(params.get("to") || "") ? params.get("to") : defaults.to;

  const [draft, setDraft] = useState({ from, to });
  const [rangeError, setRangeError] = useState("");
  const [state, setState] = useState({ report: null, loading: true, error: null });
  const latest = useRef(0);

  useEffect(() => {
    setDraft({ from, to });
  }, [from, to]);

  const load = useCallback(() => {
    const id = ++latest.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchWithAuth(`/api/admin/support/sla?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`)
      .then((report) => {
        if (id === latest.current) setState({ report, loading: false, error: null });
      })
      .catch((error) => {
        if (id === latest.current) setState((s) => ({ ...s, loading: false, error: error || "INTERNAL_ERROR" }));
      });
  }, [from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const apply = (e) => {
    e.preventDefault();
    if (!DATE_RE.test(draft.from) || !DATE_RE.test(draft.to)) {
      setRangeError("Enter both dates.");
      return;
    }
    if (draft.from > draft.to) {
      setRangeError("The start date must be on or before the end date.");
      return;
    }
    setRangeError("");
    setParams({ from: draft.from, to: draft.to }, { replace: true });
  };

  const { report, loading, error } = state;
  const counts = report?.counts || {};
  const sla = Number(report?.slaHours) || SLA_HOURS;
  const resolvedCount = Number(report?.resolutionHours?.count) || 0;
  const withinPct = pct(Number(counts.resolvedWithinSla) || 0, resolvedCount);
  const breachedList = Array.isArray(report?.breached) ? report.breached : [];
  const categoryRows = breakdownRows(report?.byCategory, TICKET_CATEGORIES, ticketCategoryLabel);
  const channelRows = breakdownRows(report?.byChannel, CHANNELS, channelLabel);
  const period = `${formatDate(`${from}T12:00:00`)} to ${formatDate(`${to}T12:00:00`)}`;

  return (
    <Box>
      <PageHeader
        title="Service levels"
        breadcrumbs={[{ label: "Support requests", to: "/admin/support" }, { label: "Service levels" }]}
        description={`How quickly support requests were answered and resolved against the ${SLA_HOURS}-hour target (MTO 10.2). Counts requests opened between the dates you choose.`}
      />

      <Card component="section" aria-labelledby="range-title" sx={{ mb: 3 }}>
        <CardContent>
          <Typography id="range-title" variant="h3" component="h2" sx={{ mb: 2 }}>
            Dates
          </Typography>
          <Box component="form" noValidate onSubmit={apply}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "flex-start" } }}>
              <TextField
                id="report-from"
                label="Opened from"
                type="date"
                value={draft.from}
                onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
                error={!!rangeError}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: draft.to || undefined } }}
              />
              <TextField
                id="report-to"
                label="Opened to"
                type="date"
                value={draft.to}
                onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
                error={!!rangeError}
                helperText="Includes this day"
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: draft.from || undefined } }}
              />
              <Button type="submit" variant="contained" sx={{ mt: { sm: 1 } }} disabled={loading}>
                Show report
              </Button>
            </Stack>
            {rangeError && (
              <Alert severity="error" role="alert" sx={{ mt: 2 }}>
                {rangeError}
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>

      {report === null && error ? (
        <ErrorState error={error} title="We couldn't build the service-level report" onRetry={load} />
      ) : report === null ? (
        <LoadingState variant="page" label="Building the service-level report…" />
      ) : (
        <>
          {error && <ErrorState compact error={error} onRetry={load} />}
          <Box sx={{ height: 8, mb: 1 }}>{loading && <LinearProgress aria-label="Updating the report" />}</Box>

          <FigureGroup
            id="sla-requests"
            title={`Requests opened ${period}`}
            description={report.generatedAt ? `Report made ${formatDateTime(report.generatedAt)}.` : undefined}
            columns={4}
          >
            <StatCard
              icon={ConfirmationNumberOutlinedIcon}
              label="Requests"
              value={formatNumber(counts.total)}
              note={`${formatNumber(counts.open)} open · ${formatNumber(counts.resolved)} resolved`}
              testId="sla-total"
            />
            <StatCard
              icon={TaskAltOutlinedIcon}
              label={`Resolved within ${sla} hours`}
              value={resolvedCount ? `${formatNumber(counts.resolvedWithinSla)} of ${formatNumber(resolvedCount)}` : DASH}
              note={withinPct === null ? "No requests resolved in this period yet." : `${withinPct}% of resolved requests`}
              progress={withinPct === null ? undefined : withinPct}
              progressLabel={`Resolved within ${sla} hours`}
              testId="sla-within"
            />
            <StatCard
              icon={WarningAmberOutlinedIcon}
              tone={Number(counts.breached) > 0 ? "warning" : "primary"}
              label={`Over ${sla} hours`}
              value={formatNumber(counts.breached)}
              note={`${formatNumber(counts.openOverdue)} still open and overdue`}
              testId="sla-breached"
            />
            <StatCard
              icon={ReplayOutlinedIcon}
              label="Reopened"
              value={formatNumber(counts.reopened)}
              note="Requests reopened after being resolved"
              testId="sla-reopened"
            />
          </FigureGroup>

          <FigureGroup
            id="sla-times"
            title="Response times"
            description="Medians: half of the requests were quicker than this."
            columns={2}
          >
            <StatCard
              icon={ForumOutlinedIcon}
              label="Median time to first staff reply"
              value={formatHours(report.firstResponseHours?.median)}
              note={spreadNote(report.firstResponseHours, "staff replies")}
            />
            <StatCard
              icon={TimerOutlinedIcon}
              label="Median time to resolve"
              value={formatHours(report.resolutionHours?.median)}
              note={spreadNote(report.resolutionHours, "resolved requests")}
            />
          </FigureGroup>

          <Box
            sx={{
              display: "grid",
              gap: 2.5,
              mb: 3,
              gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" }
            }}
          >
            <Section id="by-category" title="By kind of help">
              <BreakdownTable label="Requests by kind of help" firstColumn="Kind of help" rows={categoryRows} />
            </Section>
            <Section id="by-channel" title="By channel">
              <BreakdownTable label="Requests by channel" firstColumn="How it came in" rows={channelRows} />
            </Section>
          </Box>

          <Section
            id="breached"
            title={`Requests over ${sla} hours`}
            description="Requests that were still open after their due time, or were resolved after it."
            contentSx={{ p: { xs: 2, sm: 3 } }}
          >
            {breachedList.length === 0 ? (
              <EmptyState
                compact
                icon={InsightsOutlinedIcon}
                title={`Every request in this period met the ${sla}-hour target`}
                description="Choose other dates to check another period."
              />
            ) : (
              <ResponsiveTable label={`Requests over ${sla} hours`} minWidth={900}>
                <TableHead>
                  <TableRow>
                    <TableCell component="th" scope="col">
                      Reference
                    </TableCell>
                    <TableCell component="th" scope="col">
                      Student
                    </TableCell>
                    <TableCell component="th" scope="col">
                      Kind of help
                    </TableCell>
                    <TableCell component="th" scope="col">
                      Status
                    </TableCell>
                    <TableCell component="th" scope="col">
                      Late by
                    </TableCell>
                    <TableCell component="th" scope="col" align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {breachedList.map((t) => {
                    const who = personName(t.user, "Unknown student");
                    const reference =
                      t.reference ||
                      String(t._id || "")
                        .slice(-6)
                        .toUpperCase();
                    return (
                      <TableRow key={t._id} hover>
                        <TableCell component="th" scope="row" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                          {reference}
                        </TableCell>
                        <TableCell sx={{ overflowWrap: "anywhere" }}>
                          {who}
                          <Typography sx={{ color: "text.secondary" }}>Opened {formatDateTime(t.createdAt)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{ticketCategoryLabel(t.category)}</Typography>
                          <Typography sx={{ color: "text.secondary" }}>{channelLabel(t.channel)}</Typography>
                        </TableCell>
                        <TableCell>
                          <TicketStatusChip status={t.status} />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                          {hoursText(t.overdueHours)}
                          {t.resolvedAt ? (
                            <Typography sx={{ color: "text.secondary", fontWeight: 400 }}>
                              Resolved {formatDateTime(t.resolvedAt)}
                            </Typography>
                          ) : (
                            <Typography sx={{ color: "text.secondary", fontWeight: 400 }}>Still open</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Button component={RouterLink} to={`/admin/support/${t._id}`} variant="outlined">
                            View request
                            <Box component="span" sx={visuallyHidden}>
                              {` ${reference} from ${who}`}
                            </Box>
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </ResponsiveTable>
            )}
          </Section>
        </>
      )}
    </Box>
  );
}
